import { MatSnackBar } from '@angular/material/snack-bar';
import { AppdataService } from './../../services/app-data/appdata.service';
import {
  Component,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  OnDestroy
} from '@angular/core';
import { CommunicationInterfaceService } from 'src/app/services/communicationInterface/communication-interface.service';
import { MatDialog } from '@angular/material/dialog';
import { AnnotationDialogUtilityComponent } from './annotation-dialog-utility/annotation-dialog-utility.component';

declare var document: any;
@Component({
  selector: 'app-annotation',
  templateUrl: './annotation.component.html',
  styleUrls: ['./annotation.component.scss']
})
export class AnnotationComponent implements OnInit, OnDestroy {
  @Input('sourceImage') sourceImage!: string;
  @Input('annotationIndex') annotationIndex!: number;
  @Output('annotateObject') annotateObject = new EventEmitter<any>();
  @ViewChild('annotationImage') annotationImageEl: ElementRef;
  @ViewChild('annotationCanvas') annotationCanvasEl: ElementRef;
  appConfig: any;
  snackbarHorizontalPosition: any;
  snackbarVerticalPosition: any;
  canvasContext: any;
  isDrawing: boolean = false;
  drawMode: string = 'freedraw';
  strokeColor: string = '#000000';
  strokeThickness: number = 2;
  drawStack: any = [];
  undoDrawStack: any = [];
  redoDrawStack: any = [];
  rectCoordinates: any;
  circleCoordinates: any;
  shapeDrawDataHolder: any;
  naturalImageDimensions: { width: number; height: number } | any = undefined;
  colorHistory: any[] = ['#000000'];
  clearDiagramDialogRef: any;
  colorPickerDialogRef: any;

  constructor(
    public dialog: MatDialog,
    private appDataService: AppdataService,
    private commInterface: CommunicationInterfaceService
  ) {
    document.querySelectorAll('.cdk-overlay-container').forEach((el) => {
      el.style.zIndex = '999';
    });

    this.dialog.afterOpened.subscribe((dialog) => {
      document.querySelectorAll('.cdk-overlay-container').forEach((el) => {
        el.style.zIndex = '1000';
      });

      dialog.afterClosed().subscribe(() => {
        document.querySelectorAll('.cdk-overlay-container').forEach((el) => {
          el.style.zIndex = '999';
        });
      });
    });
  }

  ngOnDestroy(): void {
    document.querySelectorAll('.cdk-overlay-container').forEach((el) => {
      el.style.zIndex = '1000';
    });
    this.clearDiagramDialogRef?.close();
    this.colorPickerDialogRef?.close();
  }

  ngOnInit(): void {
    this.appConfig = this.appDataService.getConfig();
    this.snackbarHorizontalPosition = this.appConfig?.Miscellaneous?.snackbarPosition?.horizontalPosition || 'center';
    this.snackbarVerticalPosition = this.appConfig?.Miscellaneous?.snackbarPosition?.verticalPosition || 'top';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.clearCanvas();
    this.setCanvasSize();
    this.reDraw(this.canvasContext);
  }

  /**
   * Method to set the size of the canvas with respective to the image rendered in viewport
   */
  setCanvasSize() {
    try {
      this.annotationCanvasEl.nativeElement.width = this.annotationImageEl.nativeElement.width;
      this.annotationCanvasEl.nativeElement.height = this.annotationImageEl.nativeElement.height;
      this.canvasContext = this.annotationCanvasEl.nativeElement.getContext('2d');
      if (!this.naturalImageDimensions) {
        this.naturalImageDimensions = {};
        this.naturalImageDimensions.width = this.annotationImageEl.nativeElement.naturalWidth;
        this.naturalImageDimensions.height = this.annotationImageEl.nativeElement.naturalHeight;
      }
    } catch (error) {
      console.error('%c Error occured while setting canvas size - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'setCanvasSize', error);
    }
  }

  /**
   * Method to handle mousedown/touchstart i.e., start drawing
   * @param event Mouse/Touch events
   */
  startDrawing(event: any) {
    try {
      this.isDrawing = true;
      this.canvasContext.strokeStyle = this.strokeColor;
      this.canvasContext.lineWidth = this.strokeThickness;
      switch (this.drawMode) {
        case 'freedraw':
          {
            this.canvasContext.beginPath();
            this.canvasContext.moveTo(
              (event?.clientX ?? event?.touches[0]?.clientX) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.left,
              (event?.clientY ?? event?.touches[0]?.clientY) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.top
            );
            this.drawStack.push({
              x:
                (event?.clientX ?? event?.touches[0]?.clientX) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.left,
              y:
                (event?.clientY ?? event?.touches[0]?.clientY) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.top
            });
          }
          break;
        case 'box':
          {
            this.rectCoordinates = {
              x:
                (event?.clientX ?? event?.touches[0]?.clientX) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.left,
              y:
                (event?.clientY ?? event?.touches[0]?.clientY) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.top,
              width: 0,
              height: 0
            };
          }
          break;
        default:
          {
            this.circleCoordinates = {
              x:
                (event?.clientX ?? event?.touches[0]?.clientX) -
                this.annotationCanvasEl.nativeElement.getBoundingClientRect().left,
              y:
                (event?.clientY ?? event?.touches[0]?.clientY) -
                this.annotationCanvasEl.nativeElement.getBoundingClientRect().top,
              radius: 0
            };
          }
          break;
      }
    } catch (error) {
      console.error('%c Error occured while starting drawing - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'startDrawing', error);
    }
  }

  /**
   * Method to handle mousemove or touchmove i.e., while drawing
   * @param event Mouse/Touch events
   */
  draw(event: any) {
    try {
      if (!this.isDrawing) return;
      switch (this.drawMode) {
        case 'freedraw':
          {
            this.canvasContext.lineTo(
              (event?.clientX ?? event?.touches[0]?.clientX) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.left,
              (event?.clientY ?? event?.touches[0]?.clientY) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.top
            );
            this.canvasContext.stroke();
            this.drawStack.push({
              x:
                (event?.clientX ?? event?.touches[0]?.clientX) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.left,
              y:
                (event?.clientY ?? event?.touches[0]?.clientY) -
                this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.top
            });
          }
          break;
        case 'box':
          {
            const x =
              (event?.clientX ?? event?.touches[0]?.clientX) -
              this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.left;
            const y =
              (event?.clientY ?? event?.touches[0]?.clientY) -
              this.annotationCanvasEl?.nativeElement?.getBoundingClientRect()?.top;
            this.rectCoordinates.width = x - this.rectCoordinates?.x;
            this.rectCoordinates.height = y - this.rectCoordinates?.y;
            this.clearCanvas();
            this.shapeDrawDataHolder = {
              mode: 'box',
              strokeColor: this.strokeColor,
              strokeThickness: this.strokeThickness,
              drawnWidth: this.annotationImageEl?.nativeElement?.width,
              drawnHeight: this.annotationImageEl?.nativeElement?.height,
              data: { ...this.rectCoordinates }
            };
            this.reDraw(this.canvasContext);
            this.canvasContext.strokeStyle = this.strokeColor;
            this.canvasContext.lineWidth = this.strokeThickness;
            this.canvasContext.strokeRect(
              this.rectCoordinates?.x,
              this.rectCoordinates?.y,
              this.rectCoordinates.width,
              this.rectCoordinates.height
            );
          }
          break;
        default:
          {
            const x =
              (event?.clientX ?? event?.touches[0]?.clientX) -
              this.annotationCanvasEl.nativeElement.getBoundingClientRect().left;
            const y =
              (event?.clientY ?? event?.touches[0]?.clientY) -
              this.annotationCanvasEl.nativeElement.getBoundingClientRect().top;
            this.circleCoordinates.radius = Math.sqrt(
              Math.pow(x - this.circleCoordinates.x, 2) + Math.pow(y - this.circleCoordinates.y, 2)
            );
            this.clearCanvas();
            this.shapeDrawDataHolder = {
              mode: 'circle',
              strokeColor: this.strokeColor,
              strokeThickness: this.strokeThickness,
              drawnWidth: this.annotationImageEl.nativeElement.width,
              drawnHeight: this.annotationImageEl.nativeElement.height,
              data: { ...this.circleCoordinates }
            };
            this.reDraw(this.canvasContext);
            this.canvasContext.strokeStyle = this.strokeColor;
            this.canvasContext.lineWidth = this.strokeThickness;
            this.canvasContext.beginPath();
            this.canvasContext.arc(
              this.circleCoordinates?.x,
              this.circleCoordinates?.y,
              this.circleCoordinates?.radius,
              0,
              2 * Math.PI
            );
            this.canvasContext.stroke();
          }
          break;
      }
    } catch (error) {
      console.error('%c Error occured while drawing - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'draw', error);
    }
  }

  /**
   * Method to clear the annotation canvas
   */
  clearCanvas() {
    try {
      this.canvasContext.clearRect(
        0,
        0,
        this.annotationCanvasEl?.nativeElement?.width,
        this.annotationCanvasEl?.nativeElement?.height
      );
    } catch (error) {
      console.error('%c Error occured while clearing the canvas - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'clearCanvas', error);
    }
  }

  /**
   * Method to handle touchend and mouseup while drawing i.e., stop drawing
   */
  stopDrawing() {
    try {
      if (this.isDrawing) {
        this.isDrawing = false;
        switch (this.drawMode) {
          case 'freedraw':
            {
              this.undoDrawStack.push({
                mode: 'freedraw',
                strokeColor: this.strokeColor,
                strokeThickness: this.strokeThickness,
                drawnWidth: this.annotationImageEl?.nativeElement?.width,
                drawnHeight: this.annotationImageEl?.nativeElement?.height,
                data: this.drawStack
              });
              this.drawStack = [];
              this.redoDrawStack.length = 0;
            }
            break;
          case 'box':
            {
              if (this.rectCoordinates?.width !== 0 && this.rectCoordinates?.height !== 0) {
                this.undoDrawStack.push(this.shapeDrawDataHolder);
                this.drawStack = [];
                this.redoDrawStack.length = 0;
              }
              this.rectCoordinates = null;
            }
            break;
          default: {
            {
              if (this.circleCoordinates?.x !== 0 && this.circleCoordinates?.y !== 0) {
                this.undoDrawStack.push(this.shapeDrawDataHolder);
                this.drawStack = [];
                this.redoDrawStack.length = 0;
              }
              this.circleCoordinates = null;
            }
            break;
          }
        }
        this.clearCanvas();
        this.reDraw(this.canvasContext);
      }
    } catch (error) {
      console.error('%c Error occured while stopping drawing - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'stopDrawing', error);
    }
  }

  /**
   * Method to undo drawing
   */
  undoDraw() {
    try {
      if (this.undoDrawStack?.length > 0) {
        const lastDrawing = this.undoDrawStack.pop();
        this.redoDrawStack.push(lastDrawing);
        this.clearCanvas();
        this.reDraw(this.canvasContext);
      }
    } catch (error) {
      console.error('%c Error occured while undo drawing - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'undoDraw', error);
    }
  }

  /**
   * Method to redo the drawing
   */
  redoDraw() {
    try {
      if (this.redoDrawStack?.length > 0) {
        const nextDrawing = this.redoDrawStack.pop();
        this.undoDrawStack.push(nextDrawing);
        this.clearCanvas();
        this.reDraw(this.canvasContext);
      }
    } catch (error) {
      console.error('%c Error occured while redo drawing - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'redoDraw', error);
    }
  }

  /**
   * Method to handle redrawing of the canvas using the undoDrawStack data
   * @param canvasContext Context of the canvas element in subject
   * @param exportMode Boolean to make the drawings dynamic according to the native image size
   */
  reDraw(canvasContext: any, exportMode?: boolean) {
    try {
      canvasContext.drawImage(
        this.annotationImageEl?.nativeElement,
        0,
        0,
        !exportMode ? this.annotationImageEl?.nativeElement?.width : this.naturalImageDimensions?.width,
        !exportMode ? this.annotationImageEl?.nativeElement?.height : this.naturalImageDimensions?.height
      );
      this.undoDrawStack?.forEach((drawing) => {
        switch (drawing.mode) {
          case 'freedraw':
            {
              canvasContext.strokeStyle = drawing?.strokeColor;
              canvasContext.lineWidth = drawing?.strokeThickness;
              canvasContext.beginPath();
              canvasContext.moveTo(
                (drawing?.data[0]?.x / drawing?.drawnWidth) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.width : this.naturalImageDimensions?.width),
                (drawing?.data[0]?.y / drawing?.drawnHeight) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.height : this.naturalImageDimensions?.height)
              );
              drawing?.data?.forEach((point) => {
                canvasContext.lineTo(
                  (point?.x / drawing?.drawnWidth) *
                    (!exportMode ? this.annotationImageEl?.nativeElement?.width : this.naturalImageDimensions?.width),
                  (point?.y / drawing?.drawnHeight) *
                    (!exportMode ? this.annotationImageEl?.nativeElement?.height : this.naturalImageDimensions?.height)
                );
              });
              canvasContext.stroke();
            }
            break;
          case 'box':
            {
              canvasContext.strokeStyle = drawing?.strokeColor;
              canvasContext.lineWidth = drawing?.strokeThickness;
              canvasContext.strokeRect(
                (drawing?.data?.x / drawing?.drawnWidth) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.width : this.naturalImageDimensions?.width),
                (drawing?.data.y / drawing.drawnHeight) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.height : this.naturalImageDimensions?.height),
                (drawing?.data?.width / drawing?.drawnWidth) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.width : this.naturalImageDimensions?.width),
                (drawing?.data?.height / drawing?.drawnHeight) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.height : this.naturalImageDimensions?.height)
              );
            }
            break;
          default:
            {
              canvasContext.strokeStyle = drawing?.strokeColor;
              canvasContext.lineWidth = drawing?.strokeThickness;
              canvasContext.beginPath();
              canvasContext.arc(
                (drawing?.data?.x / drawing?.drawnWidth) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.width : this.naturalImageDimensions?.width),
                (drawing?.data?.y / drawing?.drawnHeight) *
                  (!exportMode ? this.annotationImageEl?.nativeElement?.height : this.naturalImageDimensions?.height),
                drawing?.data?.radius *
                  ((!exportMode ? this.annotationImageEl?.nativeElement?.width : this.naturalImageDimensions?.width) /
                    drawing?.drawnWidth),
                0,
                2 * Math.PI
              );
              canvasContext.stroke();
            }
            break;
        }
      });
    } catch (error) {
      console.error('%c Error occured while redrawing undoDrawStack - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'reDraw', error);
    }
  }

  /**
   * Method to export the annotated image data as base64 url
   */
  exportCanvas() {
    try {
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = this.naturalImageDimensions.width;
      exportCanvas.height = this.naturalImageDimensions.height;
      const exportCtx = exportCanvas.getContext('2d');
      this.reDraw(exportCtx, true);
      const dataURL = exportCanvas.toDataURL('image/jpeg', 1.0);
      this.annotateObject.emit({ annotatedIndex: this.annotationIndex, annotatedImage: dataURL });
    } catch (error) {
      console.error('%c Error occured while exporting annotated canvas base64 url - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'exportCanvas', error);
    }
  }

  /**
   * Method to toggle choice flex for the respective tool
   * @param className Name of the class where the choice flex to be toggled
   */
  onChoiceChange(className: string): void {
    try {
      const isExpanded = document.querySelector(`.${className}`)?.classList?.contains('expanded-choice');
      if (!isExpanded) {
        document.querySelectorAll(`.${className} .choice-flex .pre-chosen-choice-box`)?.forEach((el) => {
          el.style.transform = 'translateY(0px)';
          el.style.opacity = '1';
          el.style.pointerEvents = 'all';
        });
        document.querySelectorAll(`.${className} .choice-flex`)?.forEach((el) => {
          el.style.opacity = '1';
          el.style.pointerEvents = 'all';
        });
      } else {
        document.querySelectorAll(`.${className} .choice-flex .pre-chosen-choice-box`)?.forEach((el) => {
          el.style.transform =
            'translateY(calc(calc((var(--annotation-choice-index) * -40px) - calc(15px * var(--annotation-choice-index)))))';
          el.style.opacity = '0';
          el.style.pointerEvents = 'none';
        });
        document.querySelectorAll(`.${className} .choice-flex`)?.forEach((el) => {
          el.style.opacity = '0';
          el.style.pointerEvents = 'none';
        });
      }
      document.querySelector(`.${className}`)?.classList?.toggle('expanded-choice');
    } catch (error) {
      console.error('%c Error occured while toggling choice flex - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'onChoiceChange', error);
    }
  }

  /**
   * Method to handle property changes of the pen
   * @param className Name of the class upon which after selecting the property, closes the choice flex
   * @param property Property of the pen to be changed
   * @param value Value of the pen to be changed
   */
  onPenPropertyChange(className: string, property: string, value: string | any): void {
    try {
      switch (property) {
        case 'color':
          this.strokeColor = value;
          break;
        case 'size':
          this.strokeThickness = value;
          break;
        default:
          this.drawMode = value;
          break;
      }
      this.onChoiceChange(className);
    } catch (error) {
      console.error('%c Error occured while changing pen property - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'onPenPropertyChange', error);
    }
  }

  /**
   * Method to observe and act upon outside click other than the specified element
   * It is used for the purpose of toggling choice flexes
   * @param className Name of the class where the outside click to be observed
   */
  onOutsideClick(className) {
    try {
      document.querySelectorAll(`.${className} .choice-flex .pre-chosen-choice-box`)?.forEach((el) => {
        el.style.transform =
          'translateY(calc(calc((var(--annotation-choice-index) * -40px) - calc(15px * var(--annotation-choice-index)))))';
        el.style.opacity = '0';
        el.style.pointerEvents = 'none';
      });
      document.querySelectorAll(`.${className} .choice-flex`)?.forEach((el) => {
        el.style.opacity = '0';
        el.style.pointerEvents = 'none';
      });
      document.querySelector(`.${className}`)?.classList?.remove('expanded-choice');
    } catch (error) {
      console.error('%c Error occured while checking for outside click of action buttons - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'onOutsideClick', error);
    }
  }

  /**
   * Method to open and get data from annotation dialog color picker
   */
  onColorPickerOpen() {
    try {
      this.colorPickerDialogRef = this.dialog.open(AnnotationDialogUtilityComponent, {
        data: 'color-picker',
        disableClose: true,
        panelClass: 'always-on-top'
      });

      this.colorPickerDialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.strokeColor = result;
          this.colorHistory.unshift(result);
          if (this.colorHistory.length == 5) {
            this.colorHistory.pop();
          }
        }
      });
    } catch (error) {
      console.error('%c Error occured while opening color picker dialog - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'onColorPickerOpen', error);
    }
  }

  /**
   * Method to clear all annotation drawings
   */
  clearAnnotation() {
    try {
      this.clearDiagramDialogRef = this.dialog.open(AnnotationDialogUtilityComponent, {
        data: 'clear-confirmation',
        disableClose: true,
        panelClass: 'always-on-top'
      });

      this.clearDiagramDialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.clearCanvas();
          this.undoDrawStack = [];
          this.redoDrawStack = [];
          this.drawStack = [];
        }
      });
    } catch (error) {
      console.error('%c Error occured while clearing annotation drawing - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'clearAnnotation', error);
    }
  }

  /**
   * Method to cancel annotation window
   */
  onCancelAnnotate() {
    try {
      this.annotateObject.emit({ cancelAnnotation: true });
    } catch (error) {
      console.error('%c Error occured while cancelling annotation - ', 'color: red', error);
      this.commInterface.sendUIErrors('AnnotationComponent', 'onCancelAnnotate', error);
    }
  }
}
